/**
 * As CCBs declaradas do envio eletrônico (E177.F4, GUS-253, ADR-0130). Espelha
 * `apps/api/types/ccbs_da_abertura.py` e as rotas de `apps/api/runtime/ccbs_da_abertura.py`:
 *
 * - `POST /api/casos/{id}/abertura/ccbs` declara a CCB antes dos arquivos dela;
 * - `PATCH|DELETE /api/casos/{id}/abertura/ccbs/{ccb}` corrige o número e tira a CCB sem arquivo;
 * - `PUT|DELETE /api/casos/{id}/abertura/ccbs/{ccb}/vinculos/{documento}` vincula e desvincula;
 * - `POST /api/casos/{id}/abertura/ccbs/{ccb}/divergencia` mantém o número diante da leitura.
 *
 * O bloco de cada CCB recebe pelo recebimento de sempre, com a CCB (`abrirRecebimento`).
 */
import axios, { type AxiosInstance } from 'axios';

export interface CcbDaAberturaData {
  id: string;
  case_id: string;
  numero: string;
  numero_normalizado: string;
  ordem: number;
  criada_em: string;
  removida_em?: string | null;
  divergencia_mantida_em?: string | null;
  divergencia_lida?: string | null;
}

export type CcbErrorReason =
  | 'repetida'
  | 'com_arquivos'
  | 'concluida'
  | 'nao_encontrada'
  | 'sem_divergencia'
  | 'invalida'
  | 'network'
  | 'unknown';

export class CcbError extends Error {
  public readonly reason: CcbErrorReason;

  constructor(reason: CcbErrorReason) {
    super(reason);
    this.reason = reason;
    this.name = 'CcbError';
  }
}

const POR_DETALHE: Readonly<Record<string, CcbErrorReason>> = {
  CCB_REPETIDA: 'repetida',
  CCB_COM_ARQUIVOS: 'com_arquivos',
  ABERTURA_CONCLUIDA: 'concluida',
  CCB_NAO_ENCONTRADA: 'nao_encontrada',
  CCB_SEM_DIVERGENCIA: 'sem_divergencia',
};

function motivo(err: unknown): CcbErrorReason {
  if (!axios.isAxiosError(err)) return 'unknown';
  if (err.response === undefined) return 'network';
  const detalhe = (err.response.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detalhe === 'string' && detalhe in POR_DETALHE)
    return POR_DETALHE[detalhe] ?? 'unknown';
  return err.response.status === 422 ? 'invalida' : 'unknown';
}

async function chamar<T>(pedido: () => Promise<{ data: T }>): Promise<T> {
  try {
    return (await pedido()).data;
  } catch (err) {
    throw new CcbError(motivo(err));
  }
}

const base = (caseId: string): string => `/casos/${caseId}/abertura/ccbs`;

export function declararCcb(
  client: AxiosInstance,
  caseId: string,
  numero: string,
): Promise<CcbDaAberturaData> {
  return chamar(() => client.post<CcbDaAberturaData>(base(caseId), { numero }));
}

export function corrigirCcb(
  client: AxiosInstance,
  caseId: string,
  ccbId: string,
  numero: string,
): Promise<CcbDaAberturaData> {
  return chamar(() => client.patch<CcbDaAberturaData>(`${base(caseId)}/${ccbId}`, { numero }));
}

export async function removerCcb(
  client: AxiosInstance,
  caseId: string,
  ccbId: string,
): Promise<void> {
  await chamar(() => client.delete(`${base(caseId)}/${ccbId}`));
}

export async function vincularDocumento(
  client: AxiosInstance,
  caseId: string,
  ccbId: string,
  documentoId: string,
): Promise<void> {
  await chamar(() => client.put(`${base(caseId)}/${ccbId}/vinculos/${documentoId}`));
}

export async function desvincularDocumento(
  client: AxiosInstance,
  caseId: string,
  ccbId: string,
  documentoId: string,
): Promise<void> {
  await chamar(() => client.delete(`${base(caseId)}/${ccbId}/vinculos/${documentoId}`));
}

export function manterDivergencia(
  client: AxiosInstance,
  caseId: string,
  ccbId: string,
): Promise<CcbDaAberturaData> {
  return chamar(() => client.post<CcbDaAberturaData>(`${base(caseId)}/${ccbId}/divergencia`));
}
